import React, { useState } from "react";
import { Button, Modal, Table, Message, Progress, Label } from "semantic-ui-react";
import * as XLSX from "xlsx";
import { canonicalGroup, groupsOf } from "./appUserGroups";

import firebase from "../../../app/config/firebase";
const firestore = firebase.firestore();

// Adding travellers a few at a time keeps a 200-person group to a few minutes without
// flooding the login function
const CONCURRENCY = 3;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Lowercase and digits only, without look-alikes (0/o, 1/l/i), because travellers struggle with
// case-sensitive passwords
const PASSWORD_ALPHABET = "abcdefghjkmnpqrstuvwxyz23456789";

const HEADER_ALIASES = {
    name: ["name", "full name", "traveller", "traveler", "client name"],
    email: ["email", "email address", "e-mail"],
    password: ["password", "app password"],
    group: ["group", "group name"],
};

function generatePassword() {
    const bytes = new Uint32Array(8);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (n) => PASSWORD_ALPHABET[n % PASSWORD_ALPHABET.length]).join("");
}

// Reads the first sheet of an .xlsx/.xls/.csv file into rows keyed by our column names
function parseTravellerFile(arrayBuffer) {
    const workbook = XLSX.read(new Uint8Array(arrayBuffer), { type: "array" });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const grid = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: false });
    const headerIndex = grid.findIndex((row) => row.some((cell) => String(cell).trim() !== ""));
    if (headerIndex === -1) {
        throw new Error("The file is empty");
    }

    const headers = grid[headerIndex].map((cell) => String(cell).trim().toLowerCase());
    const columns = {};
    Object.entries(HEADER_ALIASES).forEach(([key, aliases]) => {
        columns[key] = headers.findIndex((header) => aliases.includes(header));
    });
    if (columns.name === -1 || columns.email === -1) {
        throw new Error("The file needs at least a Name and an Email column. Please use the template.");
    }

    const cell = (row, key) => (columns[key] === -1 ? "" : String(row[columns[key]] ?? "").trim());
    return grid
        .slice(headerIndex + 1)
        .map((row, index) => ({
            line: headerIndex + index + 2,
            name: cell(row, "name"),
            email: cell(row, "email"),
            password: cell(row, "password"),
            group: cell(row, "group"),
        }))
        .filter((row) => row.name || row.email || row.password);
}

// Decides what happens to each row: "add" a new traveller, "update" an existing traveller's group,
// or nothing. Existing travellers are matched by email and only ever have their group changed;
// a blank Group cell never clears an existing group.
function planRows(rows, existingByEmail, mainClientEmail) {
    const seen = new Set();
    const knownGroups = groupsOf([...existingByEmail.values()]);

    return rows.map((row) => {
        const email = row.email.toLowerCase();
        const group = canonicalGroup(row.group, knownGroups);
        if (group && !knownGroups.includes(group)) knownGroups.push(group);
        const base = { ...row, email, group, problems: [], note: null, action: null };

        if (email && seen.has(email)) return { ...base, note: "Skipped: duplicate email in file" };
        if (email) seen.add(email);

        if (email && email === mainClientEmail) return { ...base, note: "Main booking login, skipped" };

        const existing = existingByEmail.get(email);
        if (existing) {
            if (!group || group.toLowerCase() === (existing.group || "").toLowerCase()) {
                return { ...base, existing, note: "Already on the booking, no change" };
            }
            return { ...base, existing, action: "update" };
        }

        const problems = [];
        let password = row.password;
        let generated = false;
        if (!row.name) problems.push("Name missing");
        if (!EMAIL_PATTERN.test(email)) problems.push("Invalid email");
        if (!password) {
            password = generatePassword();
            generated = true;
        } else if (password.length < 6) {
            problems.push("Password under 6 characters");
        }
        return { ...base, password, generated, problems, action: problems.length === 0 ? "add" : null };
    });
}

async function addTraveller(bookingId, leadId, row) {
    const rowRef = await firestore.collection("bookings").doc(bookingId).collection("users").add({
        client_name: row.name,
        client_email: row.email,
        password: row.password,
        group: row.group,
        userType: "User",
        leadId: leadId || "",
        bookingId,
    });

    try {
        const sync = firebase.app().functions("europe-west2").httpsCallable("syncBookingAppUser");
        const result = await sync({ bookingId, rowId: rowRef.id });
        return { status: "added", otherBookings: result.data.otherBookings };
    } catch (error) {
        // Never leave a traveller in the list without a working login
        await rowRef.delete().catch(() => {});
        return { status: "failed", error: error.message };
    }
}

async function updateTravellerGroup(bookingId, row) {
    try {
        await firestore.collection("bookings").doc(bookingId).collection("users").doc(row.existing.id).update({ group: row.group });
        return { status: "updated" };
    } catch (error) {
        return { status: "failed", error: error.message };
    }
}

export default function AppUsersImport({ bookingId, leadId, client, clientUsers }) {
    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState(null);
    const [fileError, setFileError] = useState("");
    const [importing, setImporting] = useState(false);
    const [done, setDone] = useState(0);
    const [results, setResults] = useState(null);

    function reset() {
        setRows(null);
        setFileError("");
        setImporting(false);
        setDone(0);
        setResults(null);
    }

    function close() {
        if (importing) return;
        setOpen(false);
        reset();
    }

    async function handleFile(event) {
        const file = event.target.files[0];
        event.target.value = "";
        if (!file) return;
        setFileError("");
        try {
            const parsed = parseTravellerFile(await file.arrayBuffer());
            if (parsed.length === 0) {
                setFileError("No travellers found in the file");
                return;
            }
            const existingByEmail = new Map();
            (clientUsers || []).forEach((user) => {
                const email = (user.client_email || "").trim().toLowerCase();
                if (email && !existingByEmail.has(email)) existingByEmail.set(email, user);
            });
            const mainClientEmail = (client?.client_email || "").trim().toLowerCase();
            setRows(planRows(parsed, existingByEmail, mainClientEmail));
        } catch (error) {
            setFileError(error.message || "Could not read the file");
        }
    }

    const toAdd = rows ? rows.filter((r) => r.action === "add") : [];
    const toUpdate = rows ? rows.filter((r) => r.action === "update") : [];
    const work = [...toUpdate, ...toAdd];

    async function runImport() {
        setImporting(true);
        setDone(0);
        const outcome = new Map();
        let next = 0;

        async function worker() {
            while (next < work.length) {
                const row = work[next++];
                const result = row.action === "update" ? await updateTravellerGroup(bookingId, row) : await addTraveller(bookingId, leadId, row);
                outcome.set(row.line, result);
                setDone((count) => count + 1);
            }
        }
        await Promise.all(Array.from({ length: Math.min(CONCURRENCY, work.length) }, worker));

        setResults(outcome);
        setImporting(false);
    }

    function statusCell(row) {
        if (results && results.has(row.line)) {
            const r = results.get(row.line);
            if (r.status === "failed") return <Label color="red" basic size="small">Failed: {r.error}</Label>;
            if (r.status === "updated") return <Label color="blue" basic size="small">Group updated</Label>;
            return (
                <Label color="green" basic size="small">
                    Added{r.otherBookings > 0 ? ` (existing login, also on ${r.otherBookings} other booking${r.otherBookings === 1 ? "" : "s"}; password updated)` : ""}
                </Label>
            );
        }
        if (row.problems.length > 0) return <Label color="red" basic size="small">{row.problems.join(", ")}</Label>;
        if (row.action === "add") return <Label color="green" basic size="small">New: will be added</Label>;
        if (row.action === "update") {
            return (
                <Label color="blue" basic size="small">
                    Update group: {row.existing.group || "none"} → {row.group}
                </Label>
            );
        }
        return <Label basic size="small">{row.note}</Label>;
    }

    const added = results ? [...results.values()].filter((r) => r.status === "added").length : 0;
    const updated = results ? [...results.values()].filter((r) => r.status === "updated").length : 0;
    const failed = results ? [...results.values()].filter((r) => r.status === "failed").length : 0;
    const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

    return (
        <>
            <Button as="a" basic color="orange" onClick={() => setOpen(true)}>
                Upload Travellers
            </Button>

            <Modal open={open} onClose={close} closeOnDimmerClick={false} size="large">
                <Modal.Header>Upload Travellers</Modal.Header>
                <Modal.Content scrolling>
                    {!rows && (
                        <>
                            <p>
                                Upload an Excel or CSV file with one traveller per row, using the columns <strong>Name</strong>, <strong>Email</strong>,{" "}
                                <strong>Password</strong> and <strong>Group</strong>. Leave Password empty to have one created. Group is optional.
                            </p>
                            <p>
                                Travellers already on this booking are matched by email: their group is updated from the file and nothing else about
                                them changes, so you can upload the same list again to add or change groups.
                            </p>
                            <p>
                                <a href="/templates/App_Users_Template.xlsx" download>
                                    Download the template
                                </a>
                            </p>
                            <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} />
                            {fileError && <Message negative content={fileError} />}
                        </>
                    )}

                    {rows && (
                        <>
                            {!importing && !results && (
                                <Message
                                    info
                                    content={`${plural(toAdd.length, "new traveller")} to add and ${plural(toUpdate.length, "group update")}. Other rows will be left as they are.`}
                                />
                            )}
                            {importing && <Progress value={done} total={work.length} progress="ratio" color="orange" label="Saving, please keep this window open" />}
                            {results && (
                                <Message
                                    positive={failed === 0}
                                    warning={failed > 0}
                                    content={`${plural(added, "traveller")} added with working app logins, ${plural(updated, "group")} updated${
                                        failed > 0 ? `, ${failed} failed (see below)` : ""
                                    }.`}
                                />
                            )}

                            <Table compact celled size="small">
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell>Row</Table.HeaderCell>
                                        <Table.HeaderCell>Name</Table.HeaderCell>
                                        <Table.HeaderCell>Email</Table.HeaderCell>
                                        <Table.HeaderCell>Password</Table.HeaderCell>
                                        <Table.HeaderCell>Group</Table.HeaderCell>
                                        <Table.HeaderCell>Status</Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {rows.map((row) => (
                                        <Table.Row key={row.line}>
                                            <Table.Cell>{row.line}</Table.Cell>
                                            <Table.Cell>{row.name}</Table.Cell>
                                            <Table.Cell>{row.email}</Table.Cell>
                                            <Table.Cell>
                                                {row.existing ? (
                                                    <span style={{ color: "#888" }}>unchanged</span>
                                                ) : (
                                                    <>
                                                        {row.password}
                                                        {row.generated && " (created)"}
                                                    </>
                                                )}
                                            </Table.Cell>
                                            <Table.Cell>{row.group}</Table.Cell>
                                            <Table.Cell>{statusCell(row)}</Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    {rows && !importing && !results && (
                        <>
                            <Button onClick={reset}>Choose a different file</Button>
                            <Button color="orange" disabled={work.length === 0} onClick={runImport}>
                                {toAdd.length > 0 && toUpdate.length > 0
                                    ? `Add ${toAdd.length} and update ${toUpdate.length}`
                                    : toUpdate.length > 0
                                    ? `Update ${plural(toUpdate.length, "group")}`
                                    : `Add ${plural(toAdd.length, "traveller")}`}
                            </Button>
                        </>
                    )}
                    <Button onClick={close} disabled={importing}>
                        {results ? "Done" : "Cancel"}
                    </Button>
                </Modal.Actions>
            </Modal>
        </>
    );
}
